import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Types } from 'mongoose';
import { Observable } from 'rxjs';

import { ENDPOINTS, URLS } from '@shared/constants';
import {
  GenerateUsernameResponseDTO,
  IdentitySubordinatesResponseDTO,
} from '@shared/dtos';
import {
  CommonResponseDTO,
  IAddIdentityFromBulkRequest,
  IAddIdentityRequest,
  IConfigurableFieldConfigResponse,
  IConfigurableFieldValue,
  IContactNumber,
  IHierarchicalIdentityResponse,
  IIdentityResponse,
  IProfileResponse,
  IResetPasswordRequest,
  ISaaSUser,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';

import { FilteredConnectionIdentitiesWithRolesDTO } from '../../connections/services/connections.service';

export interface UpdateIdentityRequestDTO {
  salutation: string;
  first_name: string;
  last_name?: string;
  email?: string;
  reports_to_username?: string;
  username: string;
  internal_fields?: IConfigurableFieldValue[];
  phone_number?: IContactNumber[];
}

export interface GetHierarchicalIdentitiesResponseDTO {
  rootUser: IHierarchicalIdentityResponse;
  brokenUser: IHierarchicalIdentityResponse[];
}

interface IBulkUploadIdentitiesResponse {
  jobId: string;
}

@Injectable({
  providedIn: 'root',
})
export class IdentitiesService {
  constructor(private http: HttpClient) {}

  getAllIdentities(params: HttpParams): Observable<CommonResponseDTO<any>> {
    return this.http.get<CommonResponseDTO<ISaaSUser[]>>(URLS.IDENTITIES, {
      params,
    });
  }

  getAllSaaSTenantUsers(
    params: HttpParams
  ): Observable<CommonResponseDTO<ISaaSUser[]>> {
    return this.http.get<CommonResponseDTO<ISaaSUser[]>>(
      `${URLS.IDENTITIES}/tenant-users`,
      { params }
    );
  }

  getInternalFields(
    id: Types.ObjectId | string
  ): Observable<CommonResponseDTO<IdentitySubordinatesResponseDTO>> {
    const url = `${URLS.IDENTITIES_GET_INTERNAL_FIELD}/${id}`;
    return this.http.get<CommonResponseDTO<IdentitySubordinatesResponseDTO>>(
      url
    );
  }

  getAllIdentitiesNotConsideringConnections(
    params: HttpParams
  ): Observable<CommonResponseDTO<ISaaSUser[]>> {
    return this.http.get<CommonResponseDTO<ISaaSUser[]>>(
      URLS.INTERNAL_IDENTITIES_ALL,
      { params }
    );
  }

  getHierarchialIdentities(): Observable<
    CommonResponseDTO<GetHierarchicalIdentitiesResponseDTO>
  > {
    const url = generateURL({
      endpoint: ENDPOINTS.IDENTITIES_HIERARCHICAL,
      version: 2,
    });

    return this.http.get<
      CommonResponseDTO<GetHierarchicalIdentitiesResponseDTO>
    >(url, {});
  }

  getHierarchy(
    _id: Types.ObjectId | string
  ): Observable<CommonResponseDTO<GetHierarchicalIdentitiesResponseDTO>> {
    const url = `${URLS.IDENTITIES_HIERARCHY}/${_id}`;
    return this.http.get<
      CommonResponseDTO<GetHierarchicalIdentitiesResponseDTO>
    >(url);
  }

  getSubordinates(
    id: Types.ObjectId | string
  ): Observable<CommonResponseDTO<IdentitySubordinatesResponseDTO>> {
    const url = `${URLS.IDENTITIES_HIERARCHY_SUBORDINATES}/${id}`;
    return this.http.get<CommonResponseDTO<IdentitySubordinatesResponseDTO>>(
      url
    );
  }

  getDataForCSVFile(): Observable<CommonResponseDTO<ISaaSUser[]>> {
    return this.http.get<CommonResponseDTO<ISaaSUser[]>>(
      URLS.IDENTITIES_CSV,
      {}
    );
  }

  getIdentity(id: string): Observable<CommonResponseDTO<ISaaSUser>> {
    const url = `${URLS.IDENTITIES}/${id}`;
    return this.http.get<CommonResponseDTO<ISaaSUser>>(url);
  }

  createNewIdentity(
    data: IAddIdentityRequest
  ): Observable<CommonResponseDTO<ISaaSUser>> {
    return this.http.post<CommonResponseDTO<ISaaSUser>>(URLS.IDENTITIES, data);
  }

  createIdentitiesFromBulk(
    data: IAddIdentityFromBulkRequest[]
  ): Observable<CommonResponseDTO<IBulkUploadIdentitiesResponse>> {
    return this.http.post<CommonResponseDTO<IBulkUploadIdentitiesResponse>>(
      URLS.IDENTITIES_BULK,
      { data }
    );
  }

  resendVerification(id: string): Observable<CommonResponseDTO<void>> {
    const url = `${URLS.IDENTITIES}/${id}/resend`;
    return this.http.patch<CommonResponseDTO<void>>(url, id);
  }

  createMultipleIdentities(
    data: IAddIdentityRequest[]
  ): Observable<CommonResponseDTO<ISaaSUser>> {
    return this.http.post<CommonResponseDTO<ISaaSUser>>(
      URLS.IDENTITIES_MULTIPLE_IDENTITIES,
      data
    );
  }

  updateUser(
    data: IAddIdentityRequest,
    id: string
  ): Observable<CommonResponseDTO<ISaaSUser>> {
    const url = `${URLS.IDENTITIES}/${id}`;
    return this.http.patch<CommonResponseDTO<ISaaSUser>>(url, data);
  }

  deleteUser(id: string): Observable<CommonResponseDTO<ISaaSUser>> {
    const url = `${URLS.IDENTITIES}/${id}`;
    return this.http.delete<CommonResponseDTO<ISaaSUser>>(url);
  }

  restoreUser(id: string): Observable<CommonResponseDTO<ISaaSUser>> {
    const url = `${URLS.IDENTITIES}/${id}/restore`;
    return this.http.patch<CommonResponseDTO<ISaaSUser>>(url, {});
  }

  blockUser(id: string): Observable<CommonResponseDTO<ISaaSUser>> {
    const url = `${URLS.IDENTITIES}/${id}/block`;
    return this.http.patch<CommonResponseDTO<ISaaSUser>>(url, {});
  }

  unblockUser(id: string): Observable<CommonResponseDTO<ISaaSUser>> {
    const url = `${URLS.IDENTITIES}/${id}/unblock`;
    return this.http.patch<CommonResponseDTO<ISaaSUser>>(url, {});
  }

  generateUsername(
    first_name?: string,
    last_name?: string,
    email?: string
  ): Observable<CommonResponseDTO<GenerateUsernameResponseDTO>> {
    const data = {
      first_name,
      last_name,
      email,
    };
    return this.http.post<CommonResponseDTO<GenerateUsernameResponseDTO>>(
      URLS.IDENTITIES_USERNAME,
      data
    );
  }

  resetPassword(
    data: IResetPasswordRequest,
    id: string
  ): Observable<CommonResponseDTO<void>> {
    const url = `${URLS.IDENTITIES}/${id}/reset`;
    return this.http.patch<CommonResponseDTO<void>>(url, data);
  }

  getExternalFields() {
    return this.http.get<CommonResponseDTO<IConfigurableFieldConfigResponse[]>>(
      URLS.IDENTITIES_EXTERNAL_FIELDS
    );
  }

  findIdentity(
    params: HttpParams
  ): Observable<CommonResponseDTO<IIdentityResponse>> {
    const url = `${URLS.IDENTITIES}/find-identity`;
    return this.http.get<CommonResponseDTO<IIdentityResponse>>(url, { params });
  }

  // These features used only in BMS
  getConnectionByAgents(
    params?: HttpParams
  ): Observable<CommonResponseDTO<FilteredConnectionIdentitiesWithRolesDTO[]>> {
    const config = { params };
    const url = generateURL({
      endpoint: ENDPOINTS.AGENTS_CONNECTIONS,
      version: 2,
    });
    return this.http.get<
      CommonResponseDTO<FilteredConnectionIdentitiesWithRolesDTO[]>
    >(url, config);
  }

  activateUser(id: string) {
    const url = `${URLS.IDENTITIES}/${id}/activate`;
    return this.http.patch<CommonResponseDTO<ISaaSUser>>(url, {});
  }

  deactivateUser(id: string) {
    const url = `${URLS.IDENTITIES}/${id}/deactivate`;
    return this.http.patch<CommonResponseDTO<ISaaSUser>>(url, {});
  }

  getAdminIdentities(
    params: HttpParams
  ): Observable<CommonResponseDTO<IProfileResponse[]>> {
    const config = { params };
    const url = generateURL({
      endpoint: ENDPOINTS.GET_ADMINS,
      version: 2,
    });
    return this.http.get<CommonResponseDTO<IProfileResponse[]>>(url, config);
  }
}
